import { memo, useCallback } from 'react';
import { TeamTypeahead, type TeamOption } from '../../../../components/TeamTypeahead';
import './RecordTeamAssignment.css';

interface RecordTeamAssignmentProps {
  recordId: string;
  /**
   * The team currently assigned to this record, or `null` when unassigned.
   * Parent-owned so the displayed team name survives re-renders and saves.
   */
  value: TeamOption | null;
  /** True when this record is flagged unresolved by the commit gate. */
  unresolved?: boolean;
  onAssign: (recordId: string, team: TeamOption) => void;
  onClear: (recordId: string) => void;
}

/**
 * Per-record team picker for the batch review table. Reuses the shared
 * `TeamTypeahead` (org-scoped search + create) and reports assignment changes to
 * the parent, which persists them via `resolvedTeamId`.
 */
export const RecordTeamAssignment = memo(function RecordTeamAssignment({
  recordId,
  value,
  unresolved = false,
  onAssign,
  onClear,
}: RecordTeamAssignmentProps) {
  const handleSelect = useCallback(
    (team: TeamOption | null) => {
      // Explicit clear (× button) → unassign.
      if (team === null) {
        onClear(recordId);
        return;
      }
      // A record can only resolve to an existing team (needs a real Guid). The
      // typeahead's "Create new" option yields no id — ignore it here rather
      // than assign an unresolvable team.
      if (team.id) {
        onAssign(recordId, team);
      }
    },
    [recordId, onAssign, onClear]
  );

  return (
    <div
      className={`record-team-assignment${unresolved ? ' record-team-assignment--unresolved' : ''}`}
      data-testid={`record-team-assignment-${recordId}`}
    >
      <TeamTypeahead
        value={value}
        onSelect={handleSelect}
        placeholder="Assign team…"
        error={unresolved ? 'Assign a team to distribute' : undefined}
        allowCreate={false}
      />
    </div>
  );
});
